import { CatalogType } from '../enums/catalog-type.enum';

export interface CatalogTypeConfig {
	/** Discriminator value stored on `catalog_value.catalogType`. */
	type: CatalogType;
	/** Singular human label (e.g. for a "New X" button). */
	label: string;
	/** Plural human label (e.g. for a section header). */
	pluralLabel: string;
	/** One-line explanation shown in the management screen. */
	description: string;
	/**
	 * When set, this catalog is the CHILD level of a two-level hierarchy and
	 * each of its values' `parentId` points at a value of `parentType`
	 * (e.g. a model belongs to a manufacturer, a subcategory to a category).
	 * The management screen nests children under their parent and the
	 * consuming editor cascades the child dropdown off the chosen parent.
	 */
	parentType?: CatalogType;
}

/**
 * The single source of truth for every consolidated managed catalog
 * (PD-14226, Option A). Adding a future catalog is one entry here plus a
 * CatalogType enum member — the generic table, CRUD API, and management
 * screen pick it up automatically; no new table/migration/endpoint.
 */
export const CATALOG_TYPE_REGISTRY: Record<CatalogType, CatalogTypeConfig> = {
	[CatalogType.MerchandiserManufacturer]: {
		type: CatalogType.MerchandiserManufacturer,
		label: 'Merchandiser Manufacturer',
		pluralLabel: 'Merchandiser Manufacturers',
		description: 'Manufacturers of merchandiser (cooler) units.',
	},
	[CatalogType.MerchandiserModel]: {
		type: CatalogType.MerchandiserModel,
		label: 'Merchandiser Model',
		pluralLabel: 'Merchandiser Models',
		description:
			'Models of merchandiser units. Each model belongs to a manufacturer.',
		parentType: CatalogType.MerchandiserManufacturer,
	},
	[CatalogType.ClientBusinessType]: {
		type: CatalogType.ClientBusinessType,
		label: 'Business Type',
		pluralLabel: 'Business Types',
		description:
			'Suggested business types for the client form. These are non-binding suggestions — users can still enter any value, and the field is not restricted to this list.',
	},
	[CatalogType.VehicleMake]: {
		type: CatalogType.VehicleMake,
		label: 'Vehicle Make',
		pluralLabel: 'Vehicle Makes',
		description: 'Makes (manufacturers) of fleet vehicles.',
	},
	[CatalogType.VehicleModel]: {
		type: CatalogType.VehicleModel,
		label: 'Vehicle Model',
		pluralLabel: 'Vehicle Models',
		description: 'Models of fleet vehicles. Each model belongs to a make.',
		parentType: CatalogType.VehicleMake,
	},
	[CatalogType.SoftphoneCallDisposition]: {
		type: CatalogType.SoftphoneCallDisposition,
		label: 'Call Disposition',
		pluralLabel: 'Call Dispositions',
		description:
			'Outcomes a rep can pick when wrapping up a softphone call (e.g. "Spoke to customer", "Left voicemail"). Reps can still enter a custom outcome via "Other…".',
	},
	// PD-16048 — the Dispatch label lists. Order matters: the picker shows
	// values in sortOrder, so the ones drivers use most belong at the top.
	[CatalogType.DispatchAdjustmentReason]: {
		type: CatalogType.DispatchAdjustmentReason,
		label: 'Adjustment Reason',
		pluralLabel: 'Adjustment Reasons',
		description:
			'Reasons offered in the mid-route Adjust Inventory page. Drivers and warehouse managers can still type a custom reason via "Custom…".',
	},
	[CatalogType.DispatchStopOutcomeReason]: {
		type: CatalogType.DispatchStopOutcomeReason,
		label: 'Skip-Stop Reason',
		pluralLabel: 'Skip-Stop Reasons',
		description:
			'Reasons offered to the driver when a stop cannot be completed normally — customer refusal, store closed, blocked entrance. A value named "Other" reveals a free-text field in the driver picker.',
	},
	[CatalogType.DispatchPauseCategory]: {
		type: CatalogType.DispatchPauseCategory,
		label: 'Pause Category',
		pluralLabel: 'Pause Categories',
		description:
			'Reasons offered to the driver when pausing a trip — meal, fuel, mechanical. Pauses are recorded as stops on the trip timeline.',
	},
	// PD-16801 — the Log Visit lists.
	[CatalogType.ClientVisitType]: {
		type: CatalogType.ClientVisitType,
		label: 'Visit Type',
		pluralLabel: 'Visit Types',
		description:
			'How a rep made a customer visit (e.g. "On-site", "Phone"). Offered in the Log Visit dialog and filterable on the Visit Log report.',
	},
	[CatalogType.ClientVisitOutcome]: {
		type: CatalogType.ClientVisitOutcome,
		label: 'Visit Outcome',
		pluralLabel: 'Visit Outcomes',
		description:
			'What came of a customer visit (e.g. "Order placed", "Quote requested"). Offered in the Log Visit dialog and filterable on the Visit Log report.',
	},
};

/** All catalog types in registry (declaration) order. */
export const CATALOG_TYPES: ReadonlyArray<CatalogType> =
	Object.values(CatalogType);

/** Type guard for a string that may be a known catalog type. */
export function isCatalogType(value: string): value is CatalogType {
	return (CATALOG_TYPES as ReadonlyArray<string>).includes(value);
}
